<template>
  <div class="interpretation-container">
    <div class="interpretation-section">
      <h3 class="section-title">Peak Concentration Interpretation</h3>
      <div v-if="!store.peakConcentration">
        please enter a measured peak concentration !
      </div>
      <div v-else class="interpretation-content">
        <p class="interpret-item" v-html="compareToCmaxCMI"></p>
        <p class="interpret-item" v-html="compareToInterval"></p>
        <p class="interpret-item" v-html="compareToToxicity"></p>
      </div>
    </div>
  </div>
  </template>

  <script setup>
  import { computed } from "vue";
  import { useTestStore } from "@/stores/testStore";

  // Initialize the store
  const store = useTestStore();

  const parse = v => {
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };

  // Compute min and max for peak based on sheet columns
  const peakMinMax = computed(() => {
    if (!store.currentAtbDataRow) return { min: undefined, max: undefined };
    return {
      min: parse(store.currentAtbDataRow["Cmin usuel (peak)"]),
      max: parse(store.currentAtbDataRow["Cmax usuel (peak)"])
    };
  });

  // Compare Cmax/CMI ratio against target from sheet (similar to AUC/CMI)
  const compareToCmaxCMI = computed(() => {
    if (!store.currentAtbDataRow || !store.ecoff || !store.peakConcentration || !store.currentAtbDataRow["Cmax/CMI"]) {
      return '<span class="status-neutral">⚠️ Please provide required values (peak concentration and organism).</span>';
    }
    const targetRatio = parseFloat(store.currentAtbDataRow["Cmax/CMI"]);
    const currentRatio = store.peakConcentration / store.ecoff;
    if (isNaN(targetRatio)) {
      return '<span class="status-neutral">⚠️ No target ratio available for Cmax/CMI.</span>';
    }
    if (currentRatio > targetRatio) {
      return `<span class="status-success">✓ <strong>Cmax/CMI ratio</strong> : <strong>${currentRatio.toFixed(1)}</strong> is <strong>higher</strong> than target ratio : <strong>${targetRatio}</strong>. This suggests adequate exposure.</span>`;
    } else {
      return `<span class="status-warning">⚠️ <strong>Cmax/CMI ratio</strong> : <strong>${currentRatio.toFixed(1)}</strong> is <strong>lower</strong> than target ratio : <strong>${targetRatio}</strong>.</span>`;
    }
  });

  const toxicityThreshold = computed(() => {
    // Reuse toxicity fields pattern similar to InterFt, using admin mode specific keys if present
    const adminType = store.modeAdmin;
    // Prefer specific peak toxicity if present, else fall back to generic per-admin toxicity if available
    const specificKey = `Cpeak toxicité ${adminType}`;
    const typeKey = `type Cpeak toxicité`;
    if (!store.currentAtbDataRow || !(specificKey in store.currentAtbDataRow)) {
      return { value: null, type: null };
    }
    return {
      value: store.currentAtbDataRow[specificKey],
      type: store.currentAtbDataRow[typeKey]
    };
  });

  const compareToInterval = computed(() => {
    if (peakMinMax.value.min == null || peakMinMax.value.max == null) {
      return '<span class="status-neutral">⚠️ No data available for peak therapeutic range.</span>';
    }
    if (store.peakConcentration < peakMinMax.value.min) {
      return `<span class="status-warning">⚠️ <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>below</strong> the therapeutic range (${peakMinMax.value.min} - ${peakMinMax.value.max}mg/L).</span>`;
    } else if (store.peakConcentration > peakMinMax.value.max) {
      return `<span class="status-warning">⚠️ <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>over</strong> the therapeutic range (${peakMinMax.value.min} - ${peakMinMax.value.max}mg/L).</span>`;
    } else {
      return `<span class="status-success">✅ <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>within</strong> the therapeutic range (${peakMinMax.value.min} - ${peakMinMax.value.max}mg/L).</span>`;
    }
  });

  const compareToToxicity = computed(() => {
    if (!store.currentAtbDataRow || toxicityThreshold.value.value == null) {
      return `<span class="status-neutral">⚠️ <strong>Toxicity</strong> data not available for <strong>${store.modeAdmin}</strong> administration.</span>`;
    }
    if (store.peakConcentration > toxicityThreshold.value.value) {
      return `<span class="status-error">❗ <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>higher</strong> than the <strong>toxicity threshold</strong> (${toxicityThreshold.value.value}mg/L)${
        toxicityThreshold.value.type ? `, potential <strong>${toxicityThreshold.value.type}</strong> toxicity` : ``
      } for <strong>${store.modeAdmin}</strong> administration.</span>`;
    } else {
      return `<span class="status-success">✅ <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>below</strong> the <strong>toxicity threshold</strong> (${toxicityThreshold.value.value}mg/L).</span>`;
    }
  });
  </script>

  <style scoped>
  .interpretation-container {
    margin-top: 20px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .interpretation-section {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    min-width: 600px;
    box-sizing: border-box;
  }

  .section-title {
    color: #2c3e50;
    margin: 0 0 16px 0;
    font-size: 1.25rem;
    font-weight: 600;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 8px;
  }

  .interpretation-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .interpret-item {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    text-align: left;
  }

  .interpret-item strong {
    color: #2c3e50;
  }

  .status-success {
    color: #198754;
    display: block;
    padding: 8px;
    border-left: 4px solid #198754;
  }

  .status-warning {
    color: #ffc107;
    display: block;
    padding: 8px;
    border-left: 4px solid #ffc107;
  }

  .status-error {
    color: #dc3545;
    display: block;
    padding: 8px;
    border-left: 4px solid #dc3545;
  }

  .status-neutral {
    color: #6c757d;
    display: block;
    padding: 8px;
    border-left: 4px solid #6c757d;
  }
  </style>


